import { useLang } from 'hooks/lang';

import {
  actions,
  ActiveProjectInvoiceDiscountRequest,
  ProjectInvoiceDiscount,
  validateDiscountPercentageIsInvalid,
} from 'entities';

import { Alert, Card, LabeledTextarea, Button, Column, Row } from 'components/Blocks';
import { ApproveRejectButtons } from 'components/Buttons';
import { PropertyWithIcon } from 'components/PropertyWithIcon';
import { UserAvatar } from 'components/UserAvatar';
import {
  EarthIcon,
  CircleDollarIcon,
  PercentIcon,
  UserIcon,
  DollarBillIcon,
} from 'components/Icons';

import { PercentageVariation } from 'components/PercentageVariation';
import { ChooseValueModal } from './ChooseValueModal';
import { ProjectLink } from '../components';

import { useMultiStateV2, useToggleV2 } from 'hooks';
import { useUser } from 'hooks/user';

import { formatCurrency } from 'utils/text';

interface RequestProps {
  request: ActiveProjectInvoiceDiscountRequest;
  updateRequest: (params: { approved: boolean; comment: string; id: string }) => void;
}

export function SingleDiscountRequest({ request, updateRequest }: RequestProps) {
  const user = useUser();

  const {
    id,
    project,
    projectInvoice,
    requestComment,
    requestedBy,
    salesPerson,
    nonLeaderApproval,
    reviewComment,
    salesDefined,
  } = request;

  const [approved, approvedHandlers] = useToggleV2();

  const [{ comment, percentage, type, value }, { dispatch, getSetter }] = useMultiStateV2({
    percentage: request.percentage,
    value: request.value || '',
    comment: '',
    type: request.type as ProjectInvoiceDiscount['type'],
  });

  const { t } = useLang();

  const isPercent = type === 'PERCENTAGE';

  const refValue = isPercent ? Number(percentage) : Number(value);

  const fullValue = Number(projectInvoice.fullValue ?? projectInvoice.value);

  const newDiscountValue = isPercent
    ? fullValue * (Number(percentage ?? 0) / 100)
    : Number(value) || 0;

  const newDiscountPercentage = isPercent ? percentage : (newDiscountValue / fullValue) * 100;

  const finalValue = fullValue - newDiscountValue;

  const isLeader = user.type === 'LEADER';
  const isLeaderWithPreviousUserApproval = isLeader && nonLeaderApproval;

  const hasError =
    salesDefined && isPercent
      ? validateDiscountPercentageIsInvalid(percentage)
      : refValue > fullValue;

  const notFilled = [!comment.trim(), salesDefined ? !refValue : false, hasError].some(Boolean);

  const updatePrices = actions.invoice.reviewProjectInvoiceDiscountRequest.use({
    block: notFilled,

    params: {
      project: project.id,
      projectInvoice: projectInvoice.id,
      request: id,
      approved,
      comment,
      type,
      percentage: salesDefined ? percentage : undefined,
      value: salesDefined && !isPercent ? value : undefined,
    },

    onSuccess: () => updateRequest({ comment, approved, id }),
  });

  return (
    <Card flow="column" className="p-5">
      <Row justifyContent="space-between" className="small_phone:flex-col">
        <ProjectLink
          id={project.id}
          text={<span className="text-lg font-semibold text-primary">{project.name}</span>}
        />

        <UserAvatar
          user={salesPerson}
          name={t('sales-person')!}
          nameClassName="font-medium"
          nameSide="left"
          showNameOnHover
        />
      </Row>

      <p className="font-medium small_phone:text-center">{`${t('projects-client')}: ${project.clientName}`}</p>

      <Column className="border border-dashed p-4" gap="6">
        <Row justifyContent="space-between" className="small_phone:flex-col">
          <p className="text-center font-medium">{t('user-request')}</p>

          <UserAvatar user={requestedBy} name nameClassName="font-medium" nameSide="left" />
        </Row>

        <PropertyWithIcon
          icon={<UserIcon />}
          name={t('client')}
          value={projectInvoice.clientName}
        />

        <PropertyWithIcon
          icon={<EarthIcon />}
          name={t('country-to-invoice-to')}
          value={projectInvoice.country}
        />

        <PropertyWithIcon
          icon={<CircleDollarIcon />}
          name={t('full-invoice-value')}
          value={formatCurrency(fullValue, projectInvoice.currency)}
        />

        {!!projectInvoice.discount && (
          <PropertyWithIcon
            icon={<PercentIcon />}
            name={t('current-discount')}
            value={`${projectInvoice.discount}% (${projectInvoice.currency} ${projectInvoice.discountValue})`}
          />
        )}

        <PropertyWithIcon
          icon={<PercentIcon />}
          name={t('requested-discount')}
          alignItems="center"
          value={
            salesDefined ? (
              <Row gap="2">
                {!!refValue && (
                  <span>
                    {newDiscountPercentage}% ({projectInvoice.currency}{' '}
                    {newDiscountValue.toFixed(2)})
                  </span>
                )}

                <ChooseValueModal
                  type={type}
                  hasError={hasError}
                  value={isPercent ? percentage : value!}
                  update={({ type, newValue }) =>
                    dispatch({ type, percentage: newValue, value: newValue })
                  }
                />
              </Row>
            ) : (
              <Row gap="2">
                {percentage}% ({formatCurrency(newDiscountValue, projectInvoice.currency)})
                {!!projectInvoice.discountValue && (
                  <PercentageVariation
                    current={projectInvoice.discountValue}
                    newValue={newDiscountValue}
                  />
                )}
              </Row>
            )
          }
        />

        {salesDefined && (
          <Alert title={t('user-requested-a-sales-defined-discount')} level="warning">
            {t('this-request-requires-the-sales-responsible-to-define')}
          </Alert>
        )}

        <PropertyWithIcon
          icon={<DollarBillIcon />}
          name={t('final-value')}
          value={
            <Row gap="2">
              <span className="font-semibold text-primary">
                {formatCurrency(finalValue.toFixed(2), projectInvoice.currency)}
              </span>

              <PercentageVariation current={fullValue} newValue={finalValue} />
            </Row>
          }
        />

        <Alert title={t('attention')} level="info">
          {t(
            'please-be-careful-when-accepting-discounts-make-sure-the-percentage-and-values-match-what-was-described',
          )}
        </Alert>
      </Column>

      <Column gap="2">
        <p className="font-medium">{t('user-comment')}</p>

        <p className="rounded-lg border bg-muted p-3">{requestComment}</p>
      </Column>

      {isLeaderWithPreviousUserApproval && (
        <Column gap="2">
          <Alert title={t('this-request-is-pre-approved')} level="success">
            {t('it-is-now-waiting-for-a-leader-approval-to-take-effect')}
          </Alert>

          <p className="font-medium">{t('sales-comment')}</p>

          <p className="rounded-lg border bg-muted p-3">{reviewComment || 'N/A'}</p>
        </Column>
      )}

      <Column gap="6">
        <LabeledTextarea
          label={t('comments') as string}
          value={comment}
          onChangeDirect={getSetter('comment')}
        />

        <Row justifyContent="space-between" className="small_phone:flex-col">
          <ApproveRejectButtons
            value={approved}
            onChange={approved ? approvedHandlers.turnOff : approvedHandlers.turnOn}
          />

          <Button disabled={notFilled} {...updatePrices.buttonProps}>
            {t('answer-request')}
          </Button>
        </Row>

        {!isLeader && approved && (
          <Alert level="info" title={t('leader-approval-necessary')}>
            {t('this-request-will-still-be-active-until-a-sales-leader-approves-it-all-well')}
          </Alert>
        )}
      </Column>
    </Card>
  );
}
